import axios, { AxiosError } from 'axios'
import createAuthRefreshInterceptor from 'axios-auth-refresh'
import Cookies from 'js-cookie'

export const API_URL = import.meta.env.VITE_API_URL

export const getAccessToken = () => {
  return localStorage.getItem('accessToken')
}

const axiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    Authorization: `Bearer ${getAccessToken()}`,
  },
})

export const setAccessToken = (accessToken: string) => {
  axiosInstance.defaults.headers.Authorization = `Bearer ${accessToken}`
  return localStorage.setItem('accessToken', accessToken)
}

const requestRefresh = async (failedRequest: AxiosError) => {
  try {
    const response = await axios.post(`${API_URL}/auth/refresh-token`, undefined, { withCredentials: true })
    setAccessToken(response.data.accessToken)
    if (failedRequest.response?.config?.headers) {
      failedRequest.response.config.headers['Authorization'] = `Bearer ${response.data.accessToken}`
    }
  } catch (error) {
    localStorage.removeItem('accessToken')
    return Promise.reject(error)
  }
}

export const logout = () => {
  localStorage.removeItem('accessToken')
  Cookies.remove('accessToken')
  delete axiosInstance.defaults.headers.Authorization
}

createAuthRefreshInterceptor(axiosInstance, requestRefresh)

// Custom error type for API errors
interface ApiError extends Error {
  response?: { status: number }
  status?: number
  config?: { url?: string }
}

// Add response interceptor to handle 404 errors
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // Create a custom error for 404 responses that will be caught by ErrorBoundary
    if (error.response?.status === 404) {
      const notFoundError = new Error(`Resource not found: ${error.config?.url}`) as ApiError
      notFoundError.name = 'NotFoundError'
      // Preserve the original error properties
      notFoundError.response = error.response
      notFoundError.status = 404
      return Promise.reject(notFoundError)
    }
    return Promise.reject(error)
  }
)

export { axiosInstance as api }
export default axiosInstance
